<template>
	<BaseLayout background-color-class="bg-color-white" :content-full-height="true">
		<template #header>
			<HeaderBasic :cancel-link="cancelLink" />
		</template>
		<div class="opportunistic-credit-fund-join-waitlist container">
			<div class="col-8-md col-offset-2-md">
				<h1 class="overline"> Join the waitlist </h1>
				<h2 class="heading-2 my-50"> Create a free account to join the waitlist </h2>
				<p>
					In order to effectively manage capital deployment, the Opportunistic Credit Fund is only available
					periodically via investment windows. Please complete the questions below to create an account and
					join the waitlist to be notified of future investment opportunities.
				</p>
				<form @submit.prevent>
					<div class="my-200">
						<h3 class="heading-6 mb-150"> Tell us about yourself </h3>
						<div class="display-flex-md mb-200-md">
							<div class="col-6-md mb-100-smo">
								<InputField :field="v$.firstName" field-name="First Name">
									<template #label>First name</template>
									<template #input>
										<input
											v-model="v$.firstName.$model"
											data-test="first-name"
											name="firstName"
											class="input"
											type="text"
										/>
									</template>
								</InputField>
							</div>
							<div class="col-6-md ml-100-md mb-100-smo">
								<InputField :field="v$.lastName" field-name="Last Name">
									<template #label>Last name</template>
									<template #input>
										<input
											v-model="v$.lastName.$model"
											data-test="last-name"
											name="lastName"
											class="input"
											type="text"
										/>
									</template>
								</InputField>
							</div>
						</div>

						<InputField
							v-if="!appStore.isAuthenticated"
							class="mb-200-md mb-100-smo"
							:field="v$.email"
							field-name="Email Address"
							@clear-server="serverValidationHandler.clearServerErrors('email')"
						>
							<template #label>Email address</template>
							<template #input>
								<input
									v-model="v$.email.$model"
									autocomplete="email"
									name="email"
									class="input"
									type="email"
									data-test="emailInput"
								/>
							</template>
						</InputField>

						<InputField
							v-if="!appStore.isAuthenticated"
							class="mb-200-md mb-100-smo"
							:field="v$.password"
							field-name="Password"
							@clear-server="serverValidationHandler.clearServerErrors('password')"
						>
							<template #label>Password</template>
							<template #input>
								<div class="display-flex align-items-center position-relative">
									<input
										v-model="v$.password.$model"
										data-test="password"
										name="password"
										class="input"
										type="password"
									/>
								</div>
							</template>
						</InputField>

						<InputField :field="v$.phone" field-name="Phone" class="mb-200-md mb-100-smo">
							<template #label>Mobile phone number (optional)</template>
							<template #input>
								<PhoneInput v-model="v$.phone.$model" data-test="phone-input" name="phone" />
							</template>
							<template #minLength-message> Please enter a valid phone number </template>
						</InputField>
						<BasePlaceHolder
							v-if="loading"
							class="mt-150"
							:line-count="4"
							:section-count="2"
							:placehold="loading"
						/>
						<div v-else>
							<div v-if="accreditationQuestion && accreditationQuestion.responseOptions">
								<label
									:for="accreditationQuestion.name"
									class="field__label text-color-content-secondary"
									>{{ convertWordToSentenceCase(accreditationQuestion.prompt) }}
								</label>

								<p class="mt-50 body-sm">
									{{ accreditationQuestion.description }}
									<BaseButton classes="font-weight-bold text-decoration-underline" @click="learnMore">
										Learn more
									</BaseButton>
								</p>
								<InputField
									:field="v$.isAccredited"
									:field-name="accreditationQuestion.name"
									class="mb-300-md mb-200-smo mt-100"
								>
									<template #input>
										<ul class="radio-checkbox-list">
											<li
												v-for="(choice, i) in accreditationQuestion.responseOptions"
												:key="choice.name + i"
												class="radio-checkbox-item"
											>
												<label>
													<input
														v-model="v$.isAccredited.$model"
														type="radio"
														:name="choice.name"
														:value="choice.name"
														:data-test="choice.prompt"
													/>
													<span class="mask"></span>
													<span>{{ capitalize(choice.prompt) }}</span>
												</label>
											</li>
										</ul>
									</template>
								</InputField>
							</div>

							<div v-if="investmentAmountQuestion || investmentTimingQuestion">
								<h3 class="heading-6 mb-150"> Tell us about your potential OCF investment </h3>
								<div
									:class="{
										'display-flex-md': investmentAmountQuestion && investmentTimingQuestion
									}"
								>
									<div v-if="investmentAmountQuestion" class="col-6-md mb-100-smo">
										<InputField
											:field="v$.plannedAmount"
											:field-name="investmentAmountQuestion.name"
											data-test="planned-investment-input"
										>
											<template #label>{{
												convertWordToSentenceCase(investmentAmountQuestion.prompt)
											}}</template>

											<template #input>
												<input
													v-model="v$.formattedPlannedAmount.$model"
													data-test="amount-input"
													:name="investmentAmountQuestion.name"
													class="input"
													:aria-label="investmentAmountQuestion.name"
													placeholder="Enter amount"
													@keyup="updateAmount(($event.target as HTMLInputElement).value)"
												/>
											</template>
										</InputField>
									</div>

									<div
										v-if="investmentTimingQuestion && investmentTimingQuestion.responseOptions"
										class="col-6-md ml-100-md"
									>
										<InputField
											:field="v$.plannedTiming"
											:field-name="investmentTimingQuestion.name"
										>
											<template #label>{{
												convertWordToSentenceCase(investmentTimingQuestion.prompt)
											}}</template>
											<template #input>
												<div class="select-holder">
													<select
														v-model="v$.plannedTiming.$model"
														:aria-label="investmentTimingQuestion.name"
														data-test="investment-timing"
														:name="investmentTimingQuestion.name"
													>
														<option
															v-for="timing in investmentTimingQuestion.responseOptions"
															:key="timing.name"
															:selected="timing.name === v$.plannedTiming.$model"
															:value="timing.name"
														>
															{{ timing.prompt }}
														</option>
													</select>
													<div class="select-mask">
														<span class="text">{{ selectedInvestmentTimingPrompt }}</span>
													</div>
												</div>
											</template>
										</InputField>
									</div>
								</div>
							</div>
						</div>
					</div>

					<BaseButtonGroup classes="mt-300">
						<template #primary>
							<SubmitButton :v$="v$" :is-working="isWorking" data-test="submit-form" @click="submit()">
								Submit
							</SubmitButton>
						</template>
					</BaseButtonGroup>
					<p class="body-sm text-color-content-secondary mt-150">
						<span v-for="(link, index) in clientAgreementLinks" :key="`link.title${index}`">
							{{ link.prelude }}
							<BaseLink
								:link="{ target: '_blank', href: link.url, text: link.title }"
								class="text-color-current-color text-decoration-underline"
							></BaseLink> </span
						>.
					</p>
				</form>
			</div>
		</div>
	</BaseLayout>
</template>
<script setup lang="ts">
import { capitalize, currency } from '@filters/shared-filters';
import { computed, onMounted, reactive, ref, Ref } from 'vue';
import { email, integer, maxLength, minLength, required, requiredIf } from '@vuelidate/validators';
import { getItemFromLocalStorage, setItemInLocalStorage } from '@utils/web-storage';
import {
	getWaitlistQuestions,
	submitExistingUserWaitlistResponses,
	submitNewUserWaitlistResponses
} from '@api/waitlist';
import { UserWaitlistSharedRequestParams, WaitlistQuestion, WaitlistQuestionResponseOption } from 'types/waitlist';
import AccreditedInvestorsModal from '@components/modals/accredited-investors-modal.vue';
import { convertWordToSentenceCase } from '@utils/text';
import { DocumentListItem } from 'types/documents';
import { getFundriseClientAgreementDocuments } from '@api/documents';
import HeaderBasic from '@components/header/header-basic.vue';
import InputField from '@components/form/input-field.vue';
import { Link } from 'types/layout';
import { nameRegex } from '@validators/nameRegex';
import { opportunisticCreditFundId } from '@constants/offerings';
import { passwordMaxLength } from '@constants/validation';
import { passwordRegex } from '@validators/passwordRegex';
import PhoneInput from '@components/form/phone-input.vue';
import ServerValidationHandler from '@classes/ServerValidationHandler';
import { SignupFlowType } from 'types/checkout';
import SubmitButton from '@components/form/submit-button.vue';
import { useAppStore } from '@stores/app';
import { useHead } from '@unhead/vue';
import { useRouter } from 'vue-router';
import { useVuelidate } from '@vuelidate/core';

const router = useRouter();

const appStore = useAppStore();

const signupFlowType: SignupFlowType = 'PRESET_OFFERING';

const localStorageLeadCaptureKey = 'ocfLeadCapture';

const cancelLink: Link = {
	router: 'campaign-ocf'
};

const isWorking = ref(false);

const loading = ref(false);

const serverValidationHandler = ref(new ServerValidationHandler());

const waitlistQuestions: Ref<Array<WaitlistQuestion>> = ref([]);

const clientAgreementLinks: Ref<Array<DocumentListItem & { prelude: string }>> = ref([]);

const privacyPolicyAcknowledgedVersionId = ref('');

const termsAcknowledgedVersionId = ref('');

const state = reactive({
	email: '',
	firstName: '',
	formattedPlannedAmount: '',
	isAccredited: '',
	lastName: '',
	password: '',
	phone: '',
	plannedAmount: '',
	plannedTiming: ''
});

const rules = computed(() => {
	const validations = {
		firstName: {
			required,
			nameRegex
		},
		lastName: {
			required,
			nameRegex
		},
		phone: {
			minLength: minLength(10),
			maxLength: maxLength(10)
		},
		email: {},
		password: {},
		isAccredited: {
			requiredIf: requiredIf(() => !!accreditationQuestion.value && accreditationQuestion.value.required)
		},
		plannedAmount: {
			integer,
			requiredIf: requiredIf(() => !!investmentAmountQuestion.value && investmentAmountQuestion.value.required)
		},
		formattedPlannedAmount: {
			requiredIf: requiredIf(() => !!investmentAmountQuestion.value && investmentAmountQuestion.value.required)
		},
		plannedTiming: {
			requiredIf: requiredIf(() => !!investmentTimingQuestion.value && investmentTimingQuestion.value.required)
		}
	};

	if (!appStore.isAuthenticated) {
		validations.email = {
			required,
			email,
			serverError: serverValidationHandler.value.serverError('email')
		};
		validations.password = {
			required,
			maxLength: maxLength(passwordMaxLength),
			passwordRegex: passwordRegex(),
			serverError: serverValidationHandler.value.serverError('password')
		};
	}

	return validations;
});

const v$ = useVuelidate(rules, state);

const accreditationQuestion = computed((): WaitlistQuestion | undefined => {
	const question = waitlistQuestions.value.find((q) => {
		return q.name === 'Accreditation';
	});

	if (question?.responseOptions) {
		question.responseOptions = question?.responseOptions.sort(
			(a: WaitlistQuestionResponseOption, b: WaitlistQuestionResponseOption) => {
				return a.sortIndex.localeCompare(b.sortIndex);
			}
		);
	}

	return question;
});

const investmentAmountQuestion = computed((): WaitlistQuestion | undefined => {
	return waitlistQuestions.value.find((question) => {
		return question.name === 'Investment Amount';
	});
});

const investmentTimingQuestion = computed((): WaitlistQuestion | undefined => {
	const question = waitlistQuestions.value.find((q) => {
		return q.name === 'Investment Timing';
	});

	if (question?.responseOptions) {
		question.responseOptions = question?.responseOptions.sort(
			(a: WaitlistQuestionResponseOption, b: WaitlistQuestionResponseOption) => {
				return a.sortIndex.localeCompare(b.sortIndex);
			}
		);
	}

	return question;
});

const selectedInvestmentTimingPrompt = computed((): string => {
	return (
		investmentTimingQuestion.value?.responseOptions?.find((question) => question.name === state.plannedTiming)
			?.prompt ?? ''
	);
});

onMounted((): void => {
	state.email = getItemFromLocalStorage('ocfLeadCapture') ?? '';
});

function learnMore(): void {
	appStore.updateCurrentModal({
		modal: AccreditedInvestorsModal
	});
}

function updateAmount(newAmount: string): void {
	if (!newAmount.length) {
		state.formattedPlannedAmount = '';
		state.plannedAmount = '';
	} else {
		state.plannedAmount = newAmount.replace(/\D/g, '');

		if (state.plannedAmount.startsWith('0')) {
			state.plannedAmount = state.plannedAmount.slice(1);
		}

		state.formattedPlannedAmount = state.plannedAmount.length ? currency(state.plannedAmount, 0) : '';
	}

	v$.value.plannedAmount.$touch();
	v$.value.formattedPlannedAmount.$touch();
}

async function submit(): Promise<void> {
	if (!v$.value.$invalid) {
		isWorking.value = true;

		try {
			if (appStore.isAuthenticated) {
				await submitExistingUserWaitlistResponses({
					offeringId: opportunisticCreditFundId,
					...prepareSharedPayload()
				});
			} else {
				await submitNewUserWaitlistResponses({
					email: state.email,
					password: state.password,
					offeringId: opportunisticCreditFundId,
					privacyPolicyAcknowledgedVersionId: privacyPolicyAcknowledgedVersionId.value,
					termsAcknowledgedVersionId: termsAcknowledgedVersionId.value,
					...prepareSharedPayload()
				});

				setItemInLocalStorage(localStorageLeadCaptureKey, state.email);
			}
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			if (error.response?.data?.fieldErrorMap?.email) {
				appStore.addToast({
					type: 'error',
					message: `${error.response?.data?.fieldErrorMap?.email}`
				});
			}
		} finally {
			isWorking.value = false;
			await router.push({ name: 'campaign-ocf' });
		}
	}
}

function prepareSharedPayload(): UserWaitlistSharedRequestParams {
	const responses = [];

	if (accreditationQuestion.value) {
		responses.push({
			questionName: accreditationQuestion.value.name,
			response: state.isAccredited
		});
	}

	if (investmentAmountQuestion.value) {
		responses.push({
			questionName: investmentAmountQuestion.value.name,
			response: state.plannedAmount
		});
	}

	if (investmentTimingQuestion.value) {
		responses.push({
			questionName: investmentTimingQuestion.value.name,
			response: state.plannedTiming
		});
	}

	return {
		firstName: state.firstName,
		lastName: state.lastName,
		phoneNumber: state.phone,
		responses,
		signupFlowType
	};
}

async function setup(): Promise<void> {
	useHead({
		title: 'Join the waitlist | Opportunistic Credit Fund'
	});
	loading.value = true;

	try {
		const clientAgreementDocuments = await getFundriseClientAgreementDocuments();

		privacyPolicyAcknowledgedVersionId.value = clientAgreementDocuments.privacyPolicy.documentRevisionId ?? '';
		termsAcknowledgedVersionId.value = clientAgreementDocuments.termsOfService.documentRevisionId ?? '';

		clientAgreementLinks.value = [
			{
				prelude: 'By continuing you indicate you have reviewed and agree to the',
				...clientAgreementDocuments.termsOfService
			},
			{ prelude: ' and ', ...clientAgreementDocuments.privacyPolicy },
			{
				prelude: `, and have received a copy of Fundrise Advisors `,
				...clientAgreementDocuments.formAdvPart2
			},
			{ prelude: ', Fundrise Advisors ', ...clientAgreementDocuments.formCrs },
			{
				prelude: ', and Fundrise Advisors ',
				...clientAgreementDocuments.clientAgreement
			}
		];

		waitlistQuestions.value = await getWaitlistQuestions(opportunisticCreditFundId, signupFlowType);
	} catch (error) {
	} finally {
		loading.value = false;
	}
}

setup();
</script>
